import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import type { OptionItem } from '@/pages/Data/components/DownloadReportFilterSection';
import { CheckboxChangeEvent } from 'antd';
import useFilterLookups from '@/hooks/useFilterLookups';
import useInventoryFilterConfigurator from '@/pages/Inventory/hooks/useInventoryFilterConfigurator';
import { inventoryFilterLookups } from '@/pages/Inventory/utils/inventoryFiltersConfig.js';
import { ALL_ALERT_IDS } from '../../Filters/CustomAlertsFilterData.js';
import {
  CalendarFilterMenu,
  FilterOption,
  MainFilterMenu,
  SecondaryFilterOption,
} from '../../Filters/FilterDropdownMenus.js';
import SubFilterMenu from '../../Filters/components/SubFilterMenu.js';
import { filtersConfig } from '@/common/static_data';
import useFilterConfigurator from '../../Filters/hooks/useFilterConfigurator.js';
import useFilterPresets, { buildPresetOptions } from '@/hooks/useFilterPresets';
import useDateRangeApplyPost from '@/hooks/useDateRangeApplyPost.js';
import { setshipmentFilterState } from '@/redux-toolkit/slice/filterSlice.js';
import {
  HandleApplyFiltersArgs,
  ID,
  ShipmentFiltersPayload,
  ShipmentsFilterPanelProps,
  FilterItem,
  ListItem,
  FilterArrayItem,
  AppliedFilter,
  ShipmentTab,
  FilterConfigShape,
  ConfigureFilterKeyFn,
  FilterMenuItems,
} from '../types.js';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useToken } from '@/common/helper';
import { isWorldCourierCompany } from '@/common/constants/worldCourier';
import { resolvePeriodRange } from '@/pages/AiHistory/utils/resolvePeriodRange.js';
import {
  buildShipmentFiltersPayload,
  SHIPMENT_DATE_KEYS,
} from '../utils/buildShipmentFiltersPayload';
import {
  DEFAULT_FILTER_LOGIC,
  FilterLogic,
  getAppliedFilterLogic,
  normalizeFilterLogic,
  supportsFilterLogic,
} from '../../Filters/filterLogic';
import { useViewFiltersModal } from './useViewFilterModal.js';
import { dedupeById } from '../../Filters/utils/dedupeById';
import { ControlTowerView } from '../index.js';
import { DIRECT_SHIPMENT_FILTER_KEYS } from '../../Filters/utils/directFilters';
import { useGateValue } from '@statsig/react-bindings';
import { useManualShipmentCreationFlags } from '@/hooks/useManualShipmentCreationFlags';
// import { ControlTowerView } from '../index.js';
dayjs.extend(utc);
dayjs.extend(timezone);

const toArr = (value: unknown) =>
  Array.isArray(value) ? value : value != null ? [value] : [];

export type ExtendedShipmentsFilterPanelProps = ShipmentsFilterPanelProps & {
  addFilterBgColor?: string;
  addFilterFontSize?: string;
  filterConfig?: FilterConfigShape;
  configureFilterKey?: ConfigureFilterKeyFn;
  extraFilterLists?: Record<string, ListItem[]>;
  dropdownMaxHeight?: number | string;
  showSearch?: boolean;
  showSaveFilterSetLink?: boolean;
  persistState?: boolean;
  showAppliedFilterPills?: boolean;
  modalMode?: boolean;
  popupContainer?: HTMLElement | null;
  onOpenSaveFilterSet?: () => void;
  useInternalSaveFilterSetModal?: boolean;
  onDeleteSavedFilterSet?: (filterSetId: string) => Promise<void> | void;
  isShipmentToolbarFilter?: boolean;
  activeTabId?: string;
  customTabs?: ShipmentTab[];
  // This controller is shared by every savedFilterKey (shipment, ai_history,
  // gxp_reports, inventory, report) — only the real Shipments page passes
  // true. Only gates the frontend-only "Filter by" category search's
  // direct trackSearch call (MainFilterMenu); backend-driven lookups are
  // never audited from here.
  auditSearch?: boolean;
};
const EMPTY_CUSTOM_TABS: ShipmentTab[] = [];
const SHIPMENT_ACTIVITY_FILTER_GROUP = {
  group: 'Activity',
  items: [
    { id: 26, key: 'has_comments', label: 'Has comments' },
    { id: 27, key: 'unread_mentions', label: 'Unread mentions' },
  ],
};

export const useShipmentFiltersPanelController = ({
  addFilterBgColor = 'var(--light-gray)',
  addFilterFontSize = 'add-filter-font',
  filterConfig,
  configureFilterKey,
  extraFilterLists = {},
  appliedSearchQuery,
  setAppliedSearchQuery,
  sentimentSections,
  showSentimentFilter = false,
  sentimentSelected,
  setSentimentSelected,
  typeSelected,
  setTypeSelected,
  onDeleteSavedFilterSet,
  savedFilterKey,
  aiStateSections,
  showAiStateFilter = false,
  token,
  permissions,
  aiStateSelected,
  setAIStateSelected,
  appliedFilters,
  setAppliedFilters,

  filterArray,
  setFilterArray,

  setFiltersPayload,
  showSaveFilterSetLink = true,
  searchQuery,
  setSearchQuery,
  isSearchVisible,
  setIsSearchVisible,

  page,
  pageSize,

  onRunSearch,
  registerHandleApplyFilters,
  didRunInitialFetchRef,

  showSearch = true,
  persistState = true,
  showAppliedFilterPills = true,
  modalMode = false,
  popupContainer = null,
  dropdownMaxHeight,
  onOpenSaveFilterSet,
  useInternalSaveFilterSetModal = true,
  isShipmentToolbarFilter = false,

  activeTabId,
  customTabs = EMPTY_CUSTOM_TABS,
  auditSearch = false,
}: ExtendedShipmentsFilterPanelProps) => {
  const dispatch = useDispatch();
  // The alert assignment filter and the hour-based alert aging options follow
  // the alert_assignment gate, and only apply to the shipment list. The filter
  // itself also needs the company to have owner assignment turned on.
  const alertAssignmentGate = useGateValue('alert_assignment');
  const { ownerAssignmentEnabled } = useManualShipmentCreationFlags();
  const alertFiltersEnabled =
    savedFilterKey === 'shipment' && alertAssignmentGate;
  const alertAssignmentFilterEnabled =
    alertFiltersEnabled && ownerAssignmentEnabled;

  const activeFilterConfig = useMemo<FilterConfigShape>(() => {
    return filterConfig ?? filtersConfig;
  }, [filterConfig]);

  const pageTopLevelFilters = useMemo(() => {
    return activeFilterConfig.topLevelFilters;
  }, [activeFilterConfig]);

  const { decodedUserToken } = useToken();
  // RTG is offered only to World Courier companies, and never on Data
  // ('report'): its schedules run in the digest service, which can't filter
  // by RTG yet.
  const showRtgFilters =
    isWorldCourierCompany(decodedUserToken()?.enablecustomerleg) &&
    savedFilterKey !== 'report';

  const pageGroupedFilters = useMemo(() => {
    const groupedFilters = activeFilterConfig.groupedFilters.map((group) => ({
      ...group,
      items: group.items.filter(
        (item) =>
          item.key !== 'alert_assignment' || alertAssignmentFilterEnabled
      ),
    }));

    const rtgKeys = ['shipper_rtg', 'consignee_rtg'];
    const withoutKeys = (keys: string[]) =>
      groupedFilters
        .map((group) => ({
          ...group,
          items: group.items.filter((item) => !keys.includes(item.key)),
        }))
        .filter((group) => group.items.length > 0);

    // GxP, Data download and AI History keep their full catalog (incl. POD
    // status and customer ship date); only RTG is gated for them.
    if (savedFilterKey !== 'shipment') {
      return showRtgFilters ? groupedFilters : withoutKeys(rtgKeys);
    }

    const groups = withoutKeys(
      showRtgFilters
        ? ['customer_defined_ship_date']
        : ['customer_defined_ship_date', ...rtgKeys]
    );

    if (groups.some((group) => group.group === 'Activity')) {
      return groups;
    }

    const datesIndex = groups.findIndex((group) => group.group === 'Dates');
    const insertAt = datesIndex >= 0 ? datesIndex + 1 : groups.length;

    return [
      ...groups.slice(0, insertAt),
      SHIPMENT_ACTIVITY_FILTER_GROUP,
      ...groups.slice(insertAt),
    ];
  }, [
    activeFilterConfig,
    savedFilterKey,
    alertAssignmentFilterEnabled,
    showRtgFilters,
  ]);

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filtersDDL, setFilters] = useState(pageGroupedFilters);

  const [secondaryFiltersDDL, setSecondaryFiltersDDL] = useState<ListItem[]>(
    []
  );
  const [secondaryFiltersSource, setSecondaryFiltersSource] = useState<
    ListItem[]
  >([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<ListItem[]>([]);
  const [secondFilterDisplay, setSecondFilterDisplay] = useState(false);

  const [openedFromPill, setOpenedFromPill] = useState(false);
  const [filterTitle, setFilterTitle] = useState('');
  const [filterBy, setFilterBy] = useState('');
  const [filterLogic, setFilterLogic] =
    useState<FilterLogic>(DEFAULT_FILTER_LOGIC);
  const [isDatePicker, setIsDatePicker] = useState(false);

  const [alertThresholdOptions] = useState<ListItem[]>([]);
  const [alertThresholdFiltered, setAlertThresholdFiltered] = useState<
    ListItem[]
  >([]);
  const [submenuAnchor, setSubmenuAnchor] = useState<{
    left: number;
    top: number;
    el: HTMLElement;
  } | null>(null);

  const [editingId, setEditingId] = useState<string | null>(null);

  const [lastSavedFiltersSignature, setLastSavedFiltersSignature] = useState<
    string | null
  >(null);

  const pendingFilterSearchRef = useRef(false);
  const [searchPopoverOpen, setSearchPopoverOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(() =>
    typeof window === 'undefined' ? 1280 : window.innerWidth
  );

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const envView = process.env.REACT_APP_CONTROL_TOWER_VIEW as ControlTowerView;

  const isCompactSearch =
    envView === 'list' ? false : windowWidth > 766 && windowWidth < 1400;
  //const isCompactSearch = false;

  useEffect(() => {
    setFilters(pageGroupedFilters);
  }, [pageGroupedFilters]);

  // NEW: hidden tab filters helper
  const getHiddenTabFilters = useCallback(() => {
    const isCustomTab =
      activeTabId &&
      activeTabId !== 'all_shipments' &&
      activeTabId !== 'active';

    if (!isCustomTab) {
      return {
        hiddenAppliedFilters: [] as AppliedFilter[],
        hiddenFilterArray: [] as FilterArrayItem[],
      };
    }

    const matchedTab = (customTabs || []).find((tab) => tab.id === activeTabId);

    return {
      hiddenAppliedFilters: (
        matchedTab?.savedFilterSet?.appliedFilters ?? []
      ).map((filter) => ({
        ...filter,
        source: 'tab_base' as const,
      })),
      hiddenFilterArray: (matchedTab?.savedFilterSet?.filters ?? []).map(
        (filter) => ({
          ...filter,
          source: 'tab_base' as const,
        })
      ),
    };
  }, [activeTabId, customTabs]);
  const runSearchAfterFilters = useCallback(
    (
      nextFilterArray: FilterArrayItem[],
      nextAppliedFilters: AppliedFilter[]
    ) => {
      const { hiddenAppliedFilters, hiddenFilterArray } = getHiddenTabFilters();

      const payload = buildShipmentFiltersPayload(
        [...hiddenFilterArray, ...(nextFilterArray || [])],
        [...hiddenAppliedFilters, ...(nextAppliedFilters || [])]
      );

      setFiltersPayload(payload);

      const q = (searchQuery || '').trim();

      onRunSearch({
        query: q,
        check: true,
        page: 1,
        pageSize,
        calledFrom: 'filters',
        payload,
      });
    },
    [getHiddenTabFilters, setFiltersPayload, searchQuery, onRunSearch, pageSize]
  );

  const {
    presets,
    presetOptions,
    selectedPresetId,
    savePresetOpen,
    openSavePresetModal,
    closeSavePresetModal,
    setSelectedPresetId,
    handleSavePreset,
    handleDeletePresetAndSyncOptions,
    loadPresets,
  } = useFilterPresets({ type: savedFilterKey });

  const lookups = useFilterLookups();
  const { resetLookups } = lookups;

  const shipmentConfigureForFilterKey = useFilterConfigurator({
    useHourlyAlertAging: alertFiltersEnabled,
    setIsFilterOpen,
    setSecondaryFiltersDDL,
    setSecondaryFiltersSource,
    setIsDatePicker,
    setFilterTitle,
    setOptionsLoading,
    savedFilterKey,
    lookups,
    lists: {
      savedFiltersSets: presetOptions,
      ...extraFilterLists,
    },
  });

  const inventoryConfigureForFilterKey = useInventoryFilterConfigurator({
    setIsFilterOpen,
    setSecondaryFiltersDDL,
    setSecondaryFiltersSource,
    setIsDatePicker,
    setFilterTitle,
    lists: {
      savedFiltersSets: presetOptions,
      teams: lookups.allTeams || [],
      recordTypes: inventoryFilterLookups.recordTypes,
      stockVsRequiredStates: inventoryFilterLookups.stockVsRequiredStates,
      stockPlanningStates: inventoryFilterLookups.stockPlanningStates,
      notificationSetupOptions: inventoryFilterLookups.notificationSetupOptions,
      structures: inventoryFilterLookups.structures,
      ...extraFilterLists,
    },
    loaders: {
      teams: lookups.loaders?.teams,
    },
  });
  const configureForFilterKey =
    configureFilterKey ??
    (savedFilterKey === 'inventory'
      ? inventoryConfigureForFilterKey
      : shipmentConfigureForFilterKey);
  const filtersSignature = useMemo(
    () => JSON.stringify({ appliedFilters, filterArray }),
    [appliedFilters, filterArray]
  );

  const hasAnyFilters = useMemo(
    () => (appliedFilters?.length ?? 0) > 0 || (filterArray?.length ?? 0) > 0,
    [appliedFilters, filterArray]
  );

  const isFilterSetSaved =
    hasAnyFilters && lastSavedFiltersSignature === filtersSignature;

  const selectedMainFilterKeys = useMemo(() => {
    if (!isShipmentToolbarFilter) return new Set<string>();
    const keys = new Set<string>();

    (filterArray || []).forEach((filter) => {
      const key = String(filter.filterBy || '').trim();
      const values = Array.isArray(filter.filterValue)
        ? filter.filterValue
        : filter.filterValue != null
          ? [filter.filterValue]
          : [];

      if (key && values.length > 0) keys.add(key);
    });

    (appliedFilters || []).forEach((filter) => {
      const key = String(filter.filterBy || filter.dateFilter || '').trim();
      if (key && key !== 'default') keys.add(key);
    });

    return keys;
  }, [isShipmentToolbarFilter, appliedFilters, filterArray]);

  const resetFilterState = () => {
    setSelectedOptions([]);
    setFilterBy('');
    setFilters(pageGroupedFilters);
    setSecondFilterDisplay(false);
    setSecondaryFiltersDDL([]);
  };

  const onAlertTypesSearch = (q: string) => {
    const s = (q || '').trim().toLowerCase();
    if (!s) {
      setAlertThresholdFiltered(alertThresholdOptions);
      return;
    }
    const getText = (o: ListItem) => (o?.name || o?.title || '') as string;
    setAlertThresholdFiltered(
      alertThresholdOptions.filter((o) => getText(o).toLowerCase().includes(s))
    );
  };
  const hiddenBaseFilterIdsForCurrentGroup = useMemo(() => {
    if (savedFilterKey !== 'shipment' || !filterBy) return new Set<string>();

    const { hiddenAppliedFilters } = getHiddenTabFilters();

    return new Set(
      hiddenAppliedFilters
        .filter(
          (f) =>
            f.source === 'tab_base' &&
            (f.filterBy === filterBy || f.dateFilter === filterBy)
        )
        .map((f) => String(f.id))
    );
  }, [savedFilterKey, filterBy, getHiddenTabFilters]);
  const hiddenFilterKeys = useMemo(() => {
    if (savedFilterKey !== 'shipment') return new Set<string>();

    const { hiddenAppliedFilters, hiddenFilterArray } = getHiddenTabFilters();

    const hiddenBaseKeys = new Set<string>();

    hiddenAppliedFilters.forEach((f) => {
      if (f.source !== 'tab_base') return;
      const key = String(f.filterBy || f.dateFilter || '').trim();
      if (key) hiddenBaseKeys.add(key);
    });

    hiddenFilterArray.forEach((f) => {
      if (f.source !== 'tab_base') return;
      const key = String(f.filterBy || '').trim();
      if (key) hiddenBaseKeys.add(key);
    });

    const allowedToHide = ['link_code', 'ARCHIVED'];

    return new Set(allowedToHide.filter((key) => hiddenBaseKeys.has(key)));
  }, [savedFilterKey, getHiddenTabFilters]);
  const handleOpenSaveFilterSet = useCallback(() => {
    if (onOpenSaveFilterSet) {
      onOpenSaveFilterSet();
      return;
    }

    if (useInternalSaveFilterSetModal) {
      openSavePresetModal();
    }
  }, [onOpenSaveFilterSet, useInternalSaveFilterSetModal, openSavePresetModal]);

  const seedLocationSelectedOptions = useCallback(
    (fb: string) => {
      if (fb !== 'from_location' && fb !== 'to_location') return;

      const appliedForThis = (appliedFilters || []).filter(
        (a) => a.filterBy === fb || a.dateFilter === fb
      );

      if (appliedForThis.length > 0) {
        setSelectedOptions(
          appliedForThis.map((a) => ({
            id: a.id,
            name: a.name ?? a.title ?? String(a.id),
          }))
        );
        return;
      }

      setSelectedOptions([]);
    },
    [appliedFilters]
  );
  useEffect(() => {
    if (!openedFromPill) return;
    if (!secondFilterDisplay) return;
    if (!filterBy) return;
    configureForFilterKey(filterBy);
  }, [
    openedFromPill,
    secondFilterDisplay,
    filterBy,
    configureForFilterKey,
    savedFilterKey,
  ]);
  const user = decodedUserToken();
  const tz = user?.user_time_zone || dayjs.tz.guess();
  useEffect(() => {
    return () => {
      resetLookups();
    };
  }, [resetLookups]);

  useEffect(() => {
    if (!tz) return;

    const dateKeys = Array.from(SHIPMENT_DATE_KEYS);
    let nextAF = appliedFilters || [];
    let nextFA = filterArray || [];
    let changed = false;

    for (const k of dateKeys) {
      const dateApplied = (nextAF || []).filter((a) => {
        const keyMatch = a.dateFilter === k || a.filterBy === k;
        if (!keyMatch) return false;

        const p = typeof a.period === 'string' ? a.period : undefined;

        if (p && p !== 'Custom') return true;

        try {
          const parsed = JSON.parse(String(a.id));
          return parsed?.period && parsed.period !== 'Custom';
        } catch {
          return false;
        }
      });

      if (!dateApplied.length) continue;

      const recomputed = dateApplied.map((af) => {
        let p: string | undefined =
          typeof af.period === 'string' ? af.period : undefined;

        if (!p) {
          try {
            const parsed = JSON.parse(String(af.id));
            if (typeof parsed?.period === 'string') p = parsed.period;
          } catch {
            //
          }
        }

        if (!p || p === 'Custom') return af;

        const range = resolvePeriodRange(p, tz);
        if (!range) return af;
        const { start, end } = range;

        const newId = JSON.stringify({ period: p, start, end });
        if (String(newId) === String(af.id)) return af;

        changed = true;
        return { ...af, id: newId, period: p, start, end };
      });

      const keepOthers = nextAF.filter(
        (a) => !(a.dateFilter === k || a.filterBy === k)
      );
      nextAF = [...keepOthers, ...recomputed];

      const newIds = recomputed.map((r) => r.id);
      nextFA = [
        ...nextFA.filter((f) => f.filterBy !== k),
        { filterBy: k, filterValue: newIds },
      ];
    }

    if (!changed) return;

    if (didRunInitialFetchRef) {
      didRunInitialFetchRef.current = true;
    }

    setAppliedFilters(nextAF);
    setFilterArray(nextFA);
    runSearchAfterFilters(nextFA, nextAF);
  }, [
    runSearchAfterFilters,
    setAppliedFilters,
    setFilterArray,
    appliedFilters,
    filterArray,
    tz,
    didRunInitialFetchRef,
  ]);
  const [savedFiltersLoading, setSavedFiltersLoading] = useState(false);
  const handleFilterClick = async (filter: FilterOption) => {
    setOpenedFromPill(false);
    setFilterLogic(
      getAppliedFilterLogic(filter.key, appliedFilters, filterArray)
    );

    if (filter.key === 'saved_filters') {
      setFilterBy(filter.key);
      setFilterTitle(filter.label);
      setIsDatePicker(false);
      setSelectedOptions([]);
      setSecondaryFiltersSource(presetOptions);
      setSecondaryFiltersDDL(presetOptions);
      // Only show the loading shell when there is nothing to render yet,
      // so cached presets do not collapse into a spinner.
      setSavedFiltersLoading(presetOptions.length === 0);

      setSecondFilterDisplay(true);
      setIsFilterOpen(false);

      try {
        const list = await loadPresets();
        const options = buildPresetOptions(list, savedFilterKey);
        setSecondaryFiltersSource(options);
        setSecondaryFiltersDDL(options);
      } finally {
        setSavedFiltersLoading(false);
      }

      return;
    }

    if (DIRECT_SHIPMENT_FILTER_KEYS.has(filter.key)) {
      setOpenedFromPill(false);

      const option: ListItem = { id: filter.key, name: filter.label };
      const nextApplied = (() => {
        const prev = appliedFilters || [];
        const idx = prev.findIndex(
          (item) => String(item.id) === String(filter.key)
        );
        if (idx === -1) {
          return [
            ...prev,
            {
              id: filter.key,
              name: filter.label,
              filterBy: filter.key,
              source: 'manual' as const,
            },
          ];
        }
        return prev;
      })();

      const nextFilterArray: FilterArrayItem[] = (() => {
        const prev = filterArray || [];
        const others = prev.filter((f) => f.filterBy !== filter.key);
        return [
          ...others,
          {
            filterBy: filter.key,
            filterValue: [filter.key],
            source: 'manual' as const,
          },
        ];
      })();

      setSelectedOptions([option]);
      setAppliedFilters(nextApplied);
      setFilterArray(nextFilterArray);
      setSecondFilterDisplay(false);
      setIsFilterOpen(false);
      runSearchAfterFilters(nextFilterArray, nextApplied);

      return;
    }

    seedLocationSelectedOptions(filter.key);
    configureForFilterKey(filter.key);

    if (filter.key === 'alert_types') {
      setSelectedOptions([{ id: 'open', name: 'Open' }]);
    }

    // Open the submenu in the same tick as closing the main menu so the
    // shipment table never flashes through an empty overlay frame.
    setFilterBy(filter.key);
    setSecondFilterDisplay(true);
    setIsFilterOpen(false);
  };

  const handleSecondaryFilterSelect = (
    e: CheckboxChangeEvent,
    option: SecondaryFilterOption
  ) => {
    if (e?.target?.checked) {
      setSelectedOptions((prevState) => {
        const alreadyExists = prevState.findIndex(
          (item) => item.id === option.id
        );
        if (alreadyExists === -1) {
          return [...prevState, option];
        }
        return prevState;
      });
    } else {
      setSelectedOptions((prevState) =>
        prevState.filter((selectedOption) => selectedOption.id !== option.id)
      );
    }
  };

  const handleAlertThresholdSelect = (action: {
    type: string;
    item?: ListItem;
    items?: ListItem[];
  }) => {
    if (action.type === 'add' && action.item) {
      const selectedItem = action.item;
      setSelectedOptions((prev) =>
        prev.some((item) => String(item.id) === String(selectedItem.id))
          ? prev
          : [...prev, selectedItem]
      );
    } else if (action.type === 'remove' && action.item) {
      setSelectedOptions((prev) =>
        prev.filter((f) => String(f.id) !== String(action.item?.id))
      );
    } else if (action.type === 'bulk-add' && action.items) {
      const addedItems = action.items;
      setSelectedOptions((prev) => {
        const existingIds = new Set(prev.map((f) => String(f.id)));
        const newItems = addedItems.filter(
          (item) => !existingIds.has(String(item.id))
        );
        return [...prev, ...newItems];
      });
    } else if (action.type === 'bulk-remove' && action.items) {
      const removeIds = new Set(action.items.map((item) => String(item.id)));
      setSelectedOptions((prev) =>
        prev.filter((f) => !removeIds.has(String(f.id)))
      );
    }
  };

  const openSubmenuAt = (fb: string, el: EventTarget | null) => {
    setFilterLogic(getAppliedFilterLogic(fb, appliedFilters, filterArray));
    if (!el || !(el instanceof HTMLElement)) {
      setOpenedFromPill(false);
      setSecondFilterDisplay(true);
      return;
    }
    const r = el.getBoundingClientRect();
    setSubmenuAnchor({
      left: r.left,
      top: r.bottom,
      el,
    });
    setOpenedFromPill(true);
    setSecondFilterDisplay(true);
  };

  const updateFilterArrayState = (model: FilterArrayItem) => {
    let prev = filterArray ? JSON.parse(JSON.stringify(filterArray)) : [];
    const existingIndex = prev.findIndex(
      (item: FilterArrayItem) => item?.filterBy === model.filterBy
    );
    if (existingIndex !== -1) {
      const existingValues = prev[existingIndex].filterValue || [];
      const newValues = Array.isArray(model.filterValue)
        ? model.filterValue
        : [model.filterValue];
      prev[existingIndex].filterValue = Array.from(
        new Set([...existingValues, ...newValues])
      );
    } else {
      prev = [...prev, model];
    }
    setFilterArray(prev);
    return prev;
  };

  const handleApplyFilters = useCallback(
    (override: HandleApplyFiltersArgs = {}) => {
      const fb = override.filterBy || filterBy;
      const items = override.items || selectedOptions || [];
      const append = !!override.append;
      const gTitle = override.groupTitle || filterTitle;
      if (!fb) return;

      if (fb === 'alert_types') {
        const logic = normalizeFilterLogic(filterLogic);
        const selectedAlertOptions = dedupeById(items || []);
        const nextApplied = (() => {
          const keepNonAlerts = (appliedFilters || []).filter(
            (p) => p.filterBy !== fb && !ALL_ALERT_IDS.has(p.id)
          );
          const nextAlerts = selectedAlertOptions.map((o) => ({
            ...o,
            filterBy: fb,
            groupTitle: 'Alerts',
            ...(supportsFilterLogic(fb) ? { logic } : {}),
            source: 'manual' as const,
          }));
          return [...keepNonAlerts, ...nextAlerts];
        })();

        const ids = selectedAlertOptions.map((x) => x.id);
        const others = (filterArray || []).filter((f) => f.filterBy !== fb);
        const nextFilterArray: FilterArrayItem[] = ids.length
          ? [
              ...others,
              {
                filterBy: fb,
                filterValue: ids,
                ...(supportsFilterLogic(fb) ? { logic } : {}),
                source: 'manual' as const,
              },
            ]
          : others;

        setAppliedFilters(nextApplied);
        setFilterArray(nextFilterArray);

        setSecondFilterDisplay(false);
        setIsFilterOpen(false);
        runSearchAfterFilters(nextFilterArray, nextApplied);
        return;
      }

      if (append) {
        const logic = normalizeFilterLogic(filterLogic);
        const nextApplied = (() => {
          const prev = (appliedFilters || []).map((filter) =>
            supportsFilterLogic(fb) &&
            (filter.filterBy === fb || filter.dateFilter === fb)
              ? { ...filter, logic }
              : filter
          );
          const newOnes = (items || [])
            .filter((opt) => !prev.some((p) => p.id === opt.id))
            .map((o) =>
              fb === 'link_code'
                ? {
                    ...o,
                    name: ` ${o.name}`,
                    filterBy: fb,
                    groupTitle: gTitle,
                    ...(supportsFilterLogic(fb) ? { logic } : {}),
                    source: 'manual' as const,
                  }
                : {
                    ...o,
                    filterBy: fb,
                    groupTitle: gTitle,
                    ...(supportsFilterLogic(fb) ? { logic } : {}),
                    source: 'manual' as const,
                  }
            );
          return [...prev, ...newOnes];
        })();

        const prevFA = filterArray || [];
        const existing = prevFA.find((f) => f.filterBy === fb);
        const fv = existing?.filterValue;
        const existingIds = new Set<ID>(
          fv == null ? [] : Array.isArray(fv) ? fv : [fv]
        );
        (items || []).forEach((it: { id: ID }) => existingIds.add(it.id));

        const nextFilterArray: FilterArrayItem[] = [
          ...prevFA.filter((f) => f.filterBy !== fb),
          {
            filterBy: fb,
            filterValue: Array.from(existingIds),
            ...(supportsFilterLogic(fb) ? { logic } : {}),
            source: 'manual' as const,
          },
        ];

        setAppliedFilters(nextApplied);
        setFilterArray(nextFilterArray);

        setSecondFilterDisplay(false);
        setIsFilterOpen(false);

        runSearchAfterFilters(nextFilterArray, nextApplied);
        return;
      }

      const desiredIds = (items || []).map((o) => o.id);
      const logic = normalizeFilterLogic(filterLogic);
      const desiredIdSet = new Set(desiredIds);

      const appliedHere = (appliedFilters || []).filter(
        (a) => a.filterBy === fb || a.dateFilter === fb
      );
      const appliedIdSet = new Set(appliedHere.map((a) => a.id));

      const toRemoveIds = Array.from(appliedIdSet).filter(
        (id) => !desiredIdSet.has(id)
      );
      const toAddIds = Array.from(desiredIdSet).filter(
        (id) => !appliedIdSet.has(id)
      );

      const nextApplied = (() => {
        const prev = appliedFilters || [];

        const afterRemoval = prev
          .filter(
            (a) =>
              !(
                (a.filterBy === fb || a.dateFilter === fb) &&
                toRemoveIds.includes(a.id)
              )
          )
          .map((filter) =>
            supportsFilterLogic(fb) &&
            (filter.filterBy === fb || filter.dateFilter === fb)
              ? { ...filter, logic }
              : filter
          );

        const addObjs = (items || [])
          .filter((o) => toAddIds.includes(o.id))
          .map((o) =>
            fb === 'link_code'
              ? {
                  ...o,
                  name: ` ${o.name}`,
                  filterBy: fb,
                  groupTitle: gTitle,
                  ...(supportsFilterLogic(fb) ? { logic } : {}),
                  source: 'manual' as const,
                }
              : {
                  ...o,
                  filterBy: fb,
                  groupTitle: gTitle,
                  ...(supportsFilterLogic(fb) ? { logic } : {}),
                  source: 'manual' as const,
                }
          );

        return [...afterRemoval, ...addObjs];
      })();

      const others = (filterArray || []).filter((f) => f.filterBy !== fb);
      const nextFilterArray: FilterArrayItem[] = desiredIds.length
        ? [
            ...others,
            {
              filterBy: fb,
              filterValue: desiredIds,
              ...(supportsFilterLogic(fb) ? { logic } : {}),
              source: 'manual' as const,
            },
          ]
        : others;

      setAppliedFilters(nextApplied);
      setFilterArray(nextFilterArray);

      setSecondFilterDisplay(false);
      setIsFilterOpen(false);

      runSearchAfterFilters(nextFilterArray, nextApplied);
    },
    [
      filterBy,
      selectedOptions,
      filterTitle,
      filterLogic,
      appliedFilters,
      filterArray,
      setAppliedFilters,
      setFilterArray,
      runSearchAfterFilters,
    ]
  );

  useEffect(() => {
    if (registerHandleApplyFilters) {
      registerHandleApplyFilters(handleApplyFilters);
    }
  }, [handleApplyFilters, registerHandleApplyFilters]);

  const handleResetFilterGroup = () => {
    if (!filterBy) return;
    setSelectedOptions([]);

    const groupIds = new Set(
      (Array.isArray(secondaryFiltersSource) && secondaryFiltersSource.length
        ? secondaryFiltersSource
        : secondaryFiltersDDL || []
      ).map((o) => o.id)
    );

    const nextApplied = (appliedFilters || []).filter((a) => {
      const belongsByKey = a.filterBy === filterBy || a.dateFilter === filterBy;
      const belongsById = groupIds.size ? groupIds.has(a.id) : false;
      return !(belongsByKey || belongsById);
    });

    const nextFilterArray = (filterArray || []).filter(
      (f) => f.filterBy !== filterBy
    );

    setAppliedFilters(nextApplied);
    setFilterArray(nextFilterArray);

    runSearchAfterFilters(nextFilterArray, nextApplied);
  };

  const handleResetAllFilters = () => {
    setAIStateSelected?.([]);
    setSentimentSelected?.([]);
    setTypeSelected?.([]);

    setSelectedOptions([]);
    setAppliedFilters([]);
    setFilterArray([]);
    setFilterBy('');
    setSecondFilterDisplay(false);
    setIsDatePicker(false);
    setSecondaryFiltersDDL([]);
    setSecondaryFiltersSource([]);
    setFilterTitle('');
    setLastSavedFiltersSignature(null);
    setSelectedPresetId(null);

    const { hiddenAppliedFilters, hiddenFilterArray } = getHiddenTabFilters();

    const payload = buildShipmentFiltersPayload(
      hiddenFilterArray,
      hiddenAppliedFilters
    );

    setFiltersPayload(payload);

    const q = (searchQuery || '').trim();

    onRunSearch({
      query: q,
      check: true,
      page: 1,
      pageSize,
      calledFrom: 'reset_all',
      payload,
    });
  };

  const removeAppliedFilter = (
    filtersToRemove: AppliedFilter | AppliedFilter[]
  ) => {
    const filters = Array.isArray(filtersToRemove)
      ? filtersToRemove
      : [filtersToRemove];

    const clonedFilterArray = JSON.parse(
      JSON.stringify(filterArray || [])
    ) as FilterArrayItem[];

    filters.forEach((filter) => {
      let indexToUpdate = -1;
      let updatedValues: ID[] = [];

      const keyForFilter = (filter.dateFilter || filter.filterBy || '').trim();
      if (!keyForFilter) return;

      if (keyForFilter === 'missing_pod') {
        const idx = clonedFilterArray.findIndex(
          (x) => x.filterBy === 'missing_pod'
        );
        if (idx > -1) {
          clonedFilterArray.splice(idx, 1);
        }
        return;
      }

      const i = clonedFilterArray.findIndex((x) => x.filterBy === keyForFilter);
      if (i > -1) {
        indexToUpdate = i;
        const fv = clonedFilterArray[i].filterValue;
        const arr: ID[] = Array.isArray(fv) ? (fv as ID[]) : [fv as ID];
        updatedValues = arr.filter((x) => String(x) !== String(filter.id));
      }

      if (indexToUpdate > -1) {
        clonedFilterArray[indexToUpdate].filterValue = updatedValues;
      }
    });

    const cleanedFilterArray = clonedFilterArray.filter((item) => {
      const fv = item.filterValue;
      if (Array.isArray(fv)) return fv.length > 0;
      return fv != null && String(fv).trim() !== '';
    });

    const updatedAppliedFilters = (appliedFilters || []).filter((f) => {
      return !filters.some((r) => {
        if (String(r.id) !== String(f.id)) return false;

        const rKey = (r.dateFilter || r.filterBy || '').trim();
        const fKey = (f.dateFilter || f.filterBy || '').trim();

        if (rKey === 'missing_pod' && fKey === 'missing_pod') return true;

        if (rKey || fKey) return rKey === fKey;
        return true;
      });
    });

    setFilterArray(cleanedFilterArray);
    setAppliedFilters(updatedAppliedFilters);

    if (updatedAppliedFilters.length === 0) {
      setFilterArray([]);
      resetFilterState();

      const { hiddenAppliedFilters, hiddenFilterArray } = getHiddenTabFilters();
      const payload = buildShipmentFiltersPayload(
        hiddenFilterArray,
        hiddenAppliedFilters
      );
      setFiltersPayload(payload);

      const q = (searchQuery || '').trim();
      onRunSearch({
        query: q,
        check: true,
        page: 1,
        pageSize,
        calledFrom: 'filters',
        payload,
      });
      return;
    }

    runSearchAfterFilters(cleanedFilterArray, updatedAppliedFilters);
  };

  const handleSearchIconClick = () => {
    if (isCompactSearch) {
      setSearchPopoverOpen(true);
    } else {
      setIsSearchVisible(true);
    }
  };

  const handleSearch = () => {
    const q = (searchQuery || '').trim();
    const { hiddenAppliedFilters, hiddenFilterArray } = getHiddenTabFilters();

    const payload = buildShipmentFiltersPayload(
      [...hiddenFilterArray, ...(filterArray || [])],
      [...hiddenAppliedFilters, ...(appliedFilters || [])]
    );

    setFiltersPayload(payload);

    onRunSearch({
      query: q,
      check: true,
      page,
      pageSize,
      calledFrom: 'search',
      payload,
    });
  };

  const handlePopoverClose = () => {
    setSearchPopoverOpen(false);
    // Keep search applied, just close the popover UI
  };

  /** Clears the query and re-runs with an empty search, optionally collapsing the search UI. */
  const runSearchReset = (closeUi: boolean) => {
    const { hiddenAppliedFilters, hiddenFilterArray } = getHiddenTabFilters();
    const payload = buildShipmentFiltersPayload(
      [...hiddenFilterArray, ...(filterArray || [])],
      [...hiddenAppliedFilters, ...(appliedFilters || [])]
    );

    setFiltersPayload(payload);
    if (closeUi) {
      setIsSearchVisible(false);
      setSearchPopoverOpen(false);
    }
    setSearchQuery('');
    setAppliedSearchQuery?.('');

    onRunSearch({
      query: '',
      check: true,
      page,
      pageSize,
      calledFrom: closeUi ? 'handleCloseSearch' : 'resetSearch',
      payload,
    });

    if (persistState && savedFilterKey === 'shipment') {
      dispatch(
        setshipmentFilterState({
          appliedFilters,
          filterArray,
          appliedSearchQuery: '',
          searchQuery: '',
          ...(closeUi ? { isSearchVisible: false } : {}),
        })
      );
    }
  };

  const handleCloseSearch = () => runSearchReset(true);

  const handleResetSearch = () => runSearchReset(false);

  const filterArrayForDateRange: FilterItem[] = (filterArray || []).map(
    (item) => ({
      filterBy: item.filterBy,
      filterValue: Array.isArray(item.filterValue)
        ? item.filterValue.map((v) => String(v))
        : [String(item.filterValue)],
    })
  );

  const { applyDateRange } = useDateRangeApplyPost({
    appliedFilters,
    filterArray: filterArrayForDateRange,
    updateFilterArrayState,
    setAppliedFilters,
    setFilterArray,
    setShowAppliedFilters: () => {},
    setFilteredData: () => {},
    setSecondFilterDisplay,
    buildPayload: buildShipmentFiltersPayload,
    setFiltersPayload: (payload: unknown) => {
      // buildPayload above is buildShipmentFiltersPayload, so this is the
      // payload it returned: a ShipmentFiltersPayload, or null for no filters.
      setFiltersPayload(payload as ShipmentFiltersPayload | null);
    },
  });

  const handleDataFromChild = (
    payload: Parameters<
      React.ComponentProps<typeof CalendarFilterMenu>['handleDataFromChild']
    >[0]
  ) => {
    // The calendar sends the range as rangeStart / rangeEnd, which is what
    // applyDateRange reads.
    if ('rangeStart' in payload) applyDateRange(payload);
    pendingFilterSearchRef.current = true;
  };

  useEffect(() => {
    const { hiddenAppliedFilters, hiddenFilterArray } = getHiddenTabFilters();

    const payload = buildShipmentFiltersPayload(
      [...hiddenFilterArray, ...(filterArray || [])],
      [...hiddenAppliedFilters, ...(appliedFilters || [])]
    );

    setFiltersPayload(payload);

    if (pendingFilterSearchRef.current) {
      runSearchAfterFilters(filterArray, appliedFilters);
      pendingFilterSearchRef.current = false;
    }
  }, [
    filterArray,
    appliedFilters,
    setFiltersPayload,
    runSearchAfterFilters,
    getHiddenTabFilters,
  ]);

  const normalizeAppliedFilters = (list: AppliedFilter[]) => {
    return (list || []).map((af) => {
      const out = { ...af };
      if (af.dateFilter) {
        if (!out.filterBy || out.filterBy === 'default') {
          out.filterBy = af.dateFilter;
        }

        try {
          const parsed = JSON.parse(String(af.id));
          if (parsed.start && !out.start) out.start = parsed.start;
          if (parsed.end && !out.end) out.end = parsed.end;
          if (parsed.period && !out.period) out.period = parsed.period;
        } catch {
          //
        }
      }
      return out;
    }) as AppliedFilter[];
  };
  const withSourceOnAppliedFilters = (
    list: AppliedFilter[],
    source: 'saved_preset' | 'manual' | 'tab_base'
  ) =>
    (list || []).map((filter) => ({
      ...filter,
      source,
    }));

  const withSourceOnFilterArray = (
    list: FilterArrayItem[],
    source: 'saved_preset' | 'manual' | 'tab_base'
  ) =>
    (list || []).map((filter) => ({
      ...filter,
      source,
    }));
  type ResolvedPresetForApply = {
    id: string | number;
    filterArray: FilterArrayItem[];
    appliedFilters: AppliedFilter[];
  };
  const handleApplyPresetFilters = useCallback(
    (presetIds: { id: string | number }[], appendMode: boolean) => {
      const resolvePresetForApply = (
        preset: { id: string | number } & Record<string, unknown>
      ): ResolvedPresetForApply | null => {
        if (!preset) return null;

        if (savedFilterKey !== 'shipment') {
          return {
            id: preset.id,
            filterArray: ((preset as { filterArray?: FilterArrayItem[] })
              .filterArray || []) as FilterArrayItem[],
            appliedFilters: ((preset as { appliedFilters?: AppliedFilter[] })
              .appliedFilters || []) as AppliedFilter[],
          };
        }

        const option = buildPresetOptions(
          [preset as never],
          savedFilterKey
        )[0] as OptionItem & {
          filterArray?: FilterArrayItem[];
          appliedFilters?: AppliedFilter[];
        };

        return {
          id: preset.id,
          filterArray: option?.filterArray || [],
          appliedFilters: option?.appliedFilters || [],
        };
      };
      const map = new Map<string, ResolvedPresetForApply>(
        (presets || [])
          .map((p) =>
            resolvePresetForApply(
              p as { id: string | number } & Record<string, unknown>
            )
          )
          .filter((p): p is ResolvedPresetForApply => Boolean(p))
          .map((p) => [String(p.id), p])
      );

      const mergeFilterArrays = (
        a: FilterArrayItem[],
        b: FilterArrayItem[],
        source: 'saved_preset' | 'manual' | 'tab_base' = 'saved_preset'
      ) => {
        const byKey = new Map<string, Set<string | number>>();
        const metadataByKey = new Map<
          string,
          Pick<FilterArrayItem, 'logic' | 'source'>
        >();

        const addAll = (src: FilterArrayItem[]) => {
          for (const it of src || []) {
            const k = it.filterBy;
            const set = byKey.get(k) ?? new Set<string | number>();

            if (Array.isArray(it.filterValue)) {
              it.filterValue.forEach((v) => set.add(v));
            } else {
              set.add(it.filterValue);
            }

            byKey.set(k, set);
            metadataByKey.set(k, {
              ...(supportsFilterLogic(k) && it.logic
                ? { logic: normalizeFilterLogic(it.logic) }
                : {}),
              source: it.source ?? source,
            });
          }
        };

        addAll(a || []);
        addAll(b || []);

        return Array.from(byKey.entries()).map(
          ([filterBy, set]): FilterArrayItem => ({
            filterBy,
            filterValue: Array.from(set),
            ...metadataByKey.get(filterBy),
          })
        );
      };

      const mergeAppliedFilters = (a: AppliedFilter[], b: AppliedFilter[]) => {
        const seen = new Set<string>();
        const keyOf = (f: AppliedFilter): string =>
          `${f.id}::${f.filterBy ?? ''}::${f.dateFilter ?? ''}`;

        const out: AppliedFilter[] = [];
        for (const src of [a || [], b || []]) {
          for (const f of src) {
            const k = keyOf(f);
            if (!seen.has(k)) {
              seen.add(k);
              out.push(f);
            }
          }
        }
        return out;
      };

      const chosen = (presetIds || [])
        .map((it) => map.get(String(it.id)))
        .filter(Boolean)
        .map((preset) => ({
          ...preset,
          filterArray: withSourceOnFilterArray(
            (preset?.filterArray || []) as FilterArrayItem[],
            'saved_preset'
          ),
          appliedFilters: withSourceOnAppliedFilters(
            (preset?.appliedFilters || []) as AppliedFilter[],
            'saved_preset'
          ),
        }));

      if (!chosen.length) {
        setSecondFilterDisplay(false);
        setIsFilterOpen(false);
        return;
      }

      if (!appendMode && presetIds.length === 1) {
        setSelectedPresetId(String(presetIds[0].id));
      } else {
        setSelectedPresetId(null);
      }

      let nextFA: FilterArrayItem[];
      let nextAF: AppliedFilter[];

      if (appendMode) {
        nextFA = chosen.reduce(
          (acc: FilterArrayItem[], p) =>
            mergeFilterArrays(acc, p.filterArray || []),
          filterArray || []
        );
        const rawNextAF = chosen.reduce(
          (acc: AppliedFilter[], p) =>
            mergeAppliedFilters(acc, p.appliedFilters || []),
          appliedFilters || []
        );
        nextAF = normalizeAppliedFilters(rawNextAF);
      } else {
        nextFA = chosen.reduce(
          (acc: FilterArrayItem[], p) =>
            mergeFilterArrays(acc, p.filterArray || []),
          []
        );
        const rawMergedAF = chosen.reduce(
          (acc: AppliedFilter[], p) =>
            mergeAppliedFilters(acc, p.appliedFilters || []),
          []
        );
        nextAF = normalizeAppliedFilters(rawMergedAF);
      }

      const aiFromPreset = nextFA.find(
        (f) => f.filterBy === 'ai_state'
      )?.filterValue;
      const sentimentFromPreset = nextFA.find(
        (f) => f.filterBy === 'sentiment'
      )?.filterValue;

      setAIStateSelected?.(toArr(aiFromPreset));
      setSentimentSelected?.(toArr(sentimentFromPreset));

      setFilterArray(nextFA);
      setAppliedFilters(nextAF);

      const { hiddenAppliedFilters, hiddenFilterArray } = getHiddenTabFilters();
      const payload = buildShipmentFiltersPayload(
        [...hiddenFilterArray, ...nextFA],
        [...hiddenAppliedFilters, ...nextAF]
      );
      setFiltersPayload(payload);

      onRunSearch({
        query: searchQuery,
        check: true,
        page: 1,
        pageSize,
        calledFrom: 'preset',
        payload,
      });

      setSecondFilterDisplay(false);
    },
    [
      presets,
      setAIStateSelected,
      setSentimentSelected,
      setFilterArray,
      setAppliedFilters,
      getHiddenTabFilters,
      setFiltersPayload,
      onRunSearch,
      searchQuery,
      pageSize,
      savedFilterKey,
      setSelectedPresetId,
      filterArray,
      appliedFilters,
    ]
  );

  const menu = (
    <MainFilterMenu
      showAlertAssignment={alertAssignmentFilterEnabled}
      dropdownMaxHeight={dropdownMaxHeight}
      topLevelFilters={pageTopLevelFilters}
      filtersDDL={filtersDDL}
      permissions={permissions}
      handleFilterClick={handleFilterClick}
      hiddenFilterKeys={hiddenFilterKeys}
      selectedFilterKeys={selectedMainFilterKeys}
      auditSearch={auditSearch}
      includeRtgFilters={showRtgFilters}
    />
  );

  const renderSubMenu = () => (
    <SubFilterMenu
      loading={
        optionsLoading || (filterBy === 'saved_filters' && savedFiltersLoading)
      }
      hiddenOptionIds={hiddenBaseFilterIdsForCurrentGroup}
      dropdownMaxHeight={dropdownMaxHeight}
      savedFilterKey={savedFilterKey}
      hideBackArrow={openedFromPill}
      alertTypesCount={alertThresholdOptions.length}
      alertOptions={alertThresholdFiltered}
      onAlertTypesSearch={onAlertTypesSearch}
      onResetGroup={handleResetFilterGroup}
      setSelectedOptions={setSelectedOptions}
      filterBy={filterBy}
      filterTitle={filterTitle}
      filterLogic={filterLogic}
      onFilterLogicChange={setFilterLogic}
      appliedFilters={appliedFilters}
      selectedOptions={selectedOptions}
      secondaryFiltersDDL={secondaryFiltersDDL}
      secondaryFiltersSource={secondaryFiltersSource}
      token={token}
      setSecondaryFiltersSource={setSecondaryFiltersSource}
      setSecondaryFiltersDDL={setSecondaryFiltersDDL}
      handleAlertThresholdSelect={handleAlertThresholdSelect}
      handleSecondaryFilterSelect={handleSecondaryFilterSelect}
      handleApplyFilters={handleApplyFilters}
      setSecondFilterDisplay={setSecondFilterDisplay}
      setIsFilterOpen={setIsFilterOpen}
      handleApplyPresetFilters={handleApplyPresetFilters}
      onDeletePreset={async (id: string | number) => {
        const presetId = String(id);

        if (savedFilterKey === 'shipment' && onDeleteSavedFilterSet) {
          await onDeleteSavedFilterSet(presetId);
        } else {
          await handleDeletePresetAndSyncOptions(presetId, {
            filterBy,
            setSecondaryFiltersSource,
            setSecondaryFiltersDDL,
          });
        }

        if (filterBy === 'saved_filters') {
          const refreshed = await loadPresets();
          const options = buildPresetOptions(refreshed, savedFilterKey);
          setSecondaryFiltersSource(options);
          setSecondaryFiltersDDL(options);
        }
      }}
    />
  );

  const calendarMenu = () => (
    <CalendarFilterMenu
      hideBackArrow={openedFromPill}
      editingId={editingId}
      dateFilter={filterBy}
      handleDataFromChild={handleDataFromChild}
      filterTitle={filterTitle}
      setSecondFilterDisplay={setSecondFilterDisplay}
      setIsFilterOpen={setIsFilterOpen}
    />
  );

  const {
    viewFiltersTab,
    viewFiltersModalOpen,
    handleOpenViewFiltersModal,
    handleCloseViewFiltersModal,
  } = useViewFiltersModal();

  const handleOpenViewFilter = () => {
    const tabs = {
      savedFilterSet: {
        name: 'Applied filters',
        appliedFilters: appliedFilters || [],
        filters: filterArray || [],
      },
    };
    handleOpenViewFiltersModal(tabs as ShipmentTab);
  };

  const saveFilterSetMenuItems: FilterMenuItems = showSaveFilterSetLink
    ? [
        {
          key: 'save',
          label: <span className='font-12'>Save filter set</span>,
          onClick: (info) => {
            info.domEvent?.stopPropagation();
            handleOpenSaveFilterSet();
          },
        },
      ]
    : [];

  const hasSelectionFilters = Boolean(
    aiStateSelected?.length || sentimentSelected?.length || typeSelected?.length
  );
  const hasAnyActiveFilter = appliedFilters.length > 0 || hasSelectionFilters;

  const filterMenuItems: FilterMenuItems = [
    {
      key: 'edit',
      label: <span className='font-12'>Edit filter</span>,
    },
    {
      key: 'view',
      label: <span className='font-12'>View filters</span>,
      onClick: (info) => {
        info.domEvent?.stopPropagation();
        handleOpenViewFilter();
      },
    },
    ...saveFilterSetMenuItems,
    {
      key: 'reset',
      label: <span className='font-12'>Reset all</span>,
      onClick: (info) => {
        info.domEvent?.stopPropagation();
        handleResetAllFilters();
      },
    },
  ];

  return {
    addFilterBgColor,
    addFilterFontSize,
    savedFilterKey,
    modalMode,
    showSearch,
    isSearchVisible,
    isCompactSearch,
    searchPopoverOpen,
    searchQuery,
    appliedSearchQuery,
    setSearchQuery,
    setSearchPopoverOpen,
    handleSearch,
    handleCloseSearch,
    handleResetSearch,
    handlePopoverClose,
    handleSearchIconClick,
    dropdownMaxHeight,
    openedFromPill,
    submenuAnchor,
    appliedFilters,
    menu,
    isFilterOpen,
    setIsFilterOpen,
    secondFilterDisplay,
    setSecondFilterDisplay,
    filterBy,
    isDatePicker,
    calendarMenu,
    renderSubMenu,
    popupContainer,
    showAiStateFilter,
    setAIStateSelected,
    aiStateSections,
    aiStateSelected,
    showSentimentFilter,
    setSentimentSelected,
    sentimentSections,
    sentimentSelected,
    typeSelected,
    setTypeSelected,
    showAppliedFilterPills,
    hasSelectionFilters,
    hasAnyActiveFilter,
    lookups,
    showSaveFilterSetLink,
    setEditingId,
    openSubmenuAt,
    setOpenedFromPill,
    handleResetAllFilters,
    removeAppliedFilter,
    setSecondaryFiltersDDL,
    setSecondaryFiltersSource,
    setOptionsLoading,
    setIsDatePicker,
    filterTitle,
    setFilterTitle,
    setFilterBy,
    filterArray,
    handleOpenSaveFilterSet,
    isFilterSetSaved,
    useInternalSaveFilterSetModal,
    savePresetOpen,
    closeSavePresetModal,
    presets,
    selectedPresetId,
    handleSavePreset,
    setLastSavedFiltersSignature,
    filterMenuItems,
    isShipmentToolbarFilter,
    viewFiltersTab,
    viewFiltersModalOpen,
    handleCloseViewFiltersModal,
  };
};
